import React, { useMemo } from "react";
import { FeatureCollection, Geometry, Polygon } from "geojson";
import { Layer, Source } from "react-map-gl/maplibre";
import { Outlet } from "react-router";
import { useLayerEvent } from "../../../index";
import type { YieldBucket } from "../apple-count-helper";
import { calibrationColorVar } from "./calibration-colors";
import { buildBucketPaint } from "../bucket-paint";
import { colors } from "../../../../../theme/colors";
import type {
  AppleCountCalibrationFeatureCollection,
  AppleCountCalibrationFeatureProperties,
} from "../../../../../routes/customer/_customer_id/field/_field_id/result-set/calibrate/Calibration.types";
import { CutoffRange } from "../../../../../routes/customer/_customer_id/taskmap/taskmap.types";

export type SelectedAppleFeature = {
  geometry: Geometry;
  properties: AppleCountCalibrationFeatureProperties;
};

export type AppleLayerCalibrationProps = {
  geojson: AppleCountCalibrationFeatureCollection;
  cutoff?: CutoffRange;
  savedFeatureIds?: Set<number>;
  onHover?: (feature: AppleCountCalibrationFeatureProperties) => void;
  onSelectFeature?: (feature: SelectedAppleFeature) => void;
  selectedFeatureId?: string | number;
  buckets: YieldBucket[];
};

export function AppleCountCalibrationLayer({
  geojson,
  cutoff,
  onSelectFeature,
  selectedFeatureId,
  savedFeatureIds,
  buckets,
}: AppleLayerCalibrationProps): React.ReactNode {
  const rootStyle = getComputedStyle(document.documentElement);

  useLayerEvent("click", "apple-fill", (event) => {
    const feature = event.features?.at(0);
    if (!feature?.properties) return;

    const appleCount = feature.properties.apple_count;
    if (appleCount == null) return;
    onSelectFeature?.({
      geometry: feature.geometry,
      properties: feature.properties as AppleCountCalibrationFeatureProperties,
    });
  });

  useLayerEvent("mouseenter", "apple-fill", (event) => {
    event.target.getCanvas().style.cursor = "pointer";
  });

  useLayerEvent("mouseleave", "apple-fill", (event) => {
    event.target.getCanvas().style.cursor = "";
  });

  const paint = useMemo(
    () =>
      buildBucketPaint(buckets, {
        property: "apple_count",
        colorVar: calibrationColorVar,
        mode: "step",
        opacity: 0.8,
      }),
    [buckets],
  );

  const selectedFeatureCollection:
    | AppleCountCalibrationFeatureCollection
    | undefined =
    selectedFeatureId !== undefined
      ? {
          type: "FeatureCollection",
          features: geojson.features.filter(
            (feature) => feature.properties.plot_id === selectedFeatureId,
          ),
        }
      : undefined;

  const savedFeatureCollection = useMemo(():
    | FeatureCollection<Polygon, AppleCountCalibrationFeatureProperties>
    | undefined => {
    if (!savedFeatureIds) return undefined;

    return {
      type: "FeatureCollection",
      features: geojson.features.filter((f) =>
        savedFeatureIds.has(f.properties.plot_id),
      ),
    };
  }, [geojson, savedFeatureIds]);

  return (
    <>
      {selectedFeatureCollection?.features.length ? (
        <Source
          id="selected-apple-feature"
          type="geojson"
          data={selectedFeatureCollection}
        >
          <Layer
            id="selected-apple-feature-fill"
            type="fill"
            paint={{
              "fill-color": rootStyle.getPropertyValue(
                "--color-apple-selected-fill",
              ),
              "fill-opacity": 0.75,
            }}
          />
        </Source>
      ) : null}

      {savedFeatureCollection?.features.length ? (
        <Source id="saved-apple" type="geojson" data={savedFeatureCollection}>
          <Layer
            id="saved-apple-outline"
            type="line"
            paint={{
              "line-color": colors.mapStroke,
              "line-width": 2.5,
            }}
          />
        </Source>
      ) : null}

      <Source id="apple" type="geojson" data={geojson}>
        <Outlet />
        <Layer
          beforeId="result"
          type="fill"
          id="apple-fill"
          filter={[
            "all",
            ["!=", ["get", "apple_count"], 0],
            [">=", ["get", "apple_count"], cutoff ? cutoff.min : 0],
            [
              "<=",
              ["get", "apple_count"],
              cutoff ? cutoff.max : Number.MAX_VALUE,
            ],
          ]}
          paint={paint}
        />
      </Source>
    </>
  );
}
